import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Card, IconButton, InputAdornment, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate, useSearchParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { ReadinessChip } from '../merchants/shared/StateChip'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants } from './ptlSetupApi'
import { ROUTES, actionLabel, fmtDate, num, zoneMapTabs } from './constants'
import { Banner, MerchantPickerDialog, merchantLabel } from './components/SetupUi'
import PtlPage from './shell/PtlPage'

/** List views (?view=). "Reset" = had its own map, now back on the standard (history kept). */
const VIEWS = [
  { key: 'own', label: 'With own map', test: (m) => (m.ownZoneMapPincodes || 0) > 0 },
  { key: 'reset', label: 'Back on standard', test: (m) => !m.ownZoneMapPincodes && !!m.zoneMapLatest },
]

const byNewest = (a, b) => new Date(b.zoneMapLatest?.createdAt || 0) - new Date(a.zoneMapLatest?.createdAt || 0)

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant Zone Map → Overrides (plan screen 6). Merchants whose own zone map
 * replaces the standard zone of some pincodes (ptl_merchant_zone_map), with KPI cards, search, and Add override. A row
 * opens that merchant's map (screen 7). ?view= and ?q= keep the list in the link.
 */
export default function PtlZoneOverrides() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  const [picking, setPicking] = useState(false)

  const view = VIEWS.some((v) => v.key === params.get('view')) ? params.get('view') : 'own'
  const search = params.get('q') || ''
  const setParam = (k, v) => {
    const next = new URLSearchParams(params)
    if (v && !(k === 'view' && v === 'own')) next.set(k, v)
    else next.delete(k)
    setParams(next, { replace: true })
  }

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then(setMerchants).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { load() }, [load])

  const all = useMemo(() => merchants || [], [merchants])
  const own = all.filter(VIEWS[0].test)
  const reset = all.filter(VIEWS[1].test)
  const latest = [...all].filter((m) => m.zoneMapLatest).sort(byNewest)[0]
  const open = (m) => navigate(`${ROUTES.zoneOverrides}/${m.id}`)

  const rows = useMemo(() => {
    const v = VIEWS.find((x) => x.key === view)
    const q = search.trim().toLowerCase()
    return all.filter((m) => v.test(m) && (!q || merchantLabel(m).toLowerCase().includes(q) || m.id.toLowerCase().includes(q))).sort(byNewest)
  }, [all, view, search])

  const kpis = [
    { key: 'own', label: 'Merchants with own map', value: num(own.length), sub: merchants ? `Of ${num(all.length)} PTL merchants` : '',
      active: view === 'own', onClick: () => setParam('view', 'own') },
    { key: 'pincodes', label: 'Pincodes overridden', value: num(own.reduce((s, m) => s + (m.ownZoneMapPincodes || 0), 0)), sub: 'Across those merchants' },
    { key: 'reset', label: 'Back on standard', value: num(reset.length), sub: 'Had their own map, history kept',
      active: view === 'reset', onClick: () => setParam('view', 'reset') },
    { key: 'last', label: 'Last change', value: latest ? fmtDate(latest.zoneMapLatest.createdAt).split(',')[0] : 'None',
      sub: latest ? `${latest.name}${latest.zoneMapLatest.createdBy ? ` · ${latest.zoneMapLatest.createdBy}` : ''}` : 'No merchant map yet' },
  ]

  return (
    <PtlPage title='Merchant Zone Map' tabTitle='Zone Map Overrides' tabs={zoneMapTabs(merchants ? own.length : undefined)}
      description='A merchant override gives one merchant its own zone for some pincodes; every other pincode keeps the standard zone. Only that merchant’s shipments use it.'
      actions={<Button variant='contained' startIcon={<AddIcon />} disabled={!merchants} onClick={() => setPicking(true)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>Add override</Button>}
      kpis={kpis} kpisLoading={!merchants && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      {merchants && own.length === 0 && reset.length === 0 ? (
        <EmptyState title='No merchant overrides yet'
          body='Every PTL merchant uses the standard zone map. Add an override when a merchant’s contract maps some pincodes to other zones.'
          action={<Button variant='contained' startIcon={<AddIcon />} onClick={() => setPicking(true)} sx={SX.cta}>Add override</Button>} />
      ) : (
        <Card elevation={0} sx={{ ...SX.card, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${C.line}`, bgcolor: C.surfaceSunken }}>
            <TextField size='small' placeholder='Search merchant or id' value={search} onChange={(e) => setParam('q', e.target.value)}
              sx={{ width: { xs: '100%', sm: 300 }, bgcolor: C.surface }} inputProps={{ 'aria-label': 'Search merchant' }}
              InputProps={{
                startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 18, color: C.inkGhost }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position='end'>
                    <IconButton size='small' aria-label='Clear search' edge='end' onClick={() => setParam('q', '')}><ClearIcon fontSize='small' /></IconButton>
                  </InputAdornment>
                ) : null,
              }} />
            <Typography role='status' sx={{ ...TYPE.rowHelp, color: C.inkMuted }}>
              {merchants ? `${num(rows.length)} merchant${rows.length === 1 ? '' : 's'} · ${VIEWS.find((v) => v.key === view).label}` : 'Loading merchants…'}
            </Typography>
          </Box>
          {merchants && rows.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <EmptyState dense title='No merchants match' body={search ? 'Try another name or id, or clear the search.' : 'No merchant is in this view.'} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 680 }} aria-label='Merchants with a zone map override'>
                <TableHead>
                  <TableRow>
                    {['Merchant', 'Own pincodes', 'Last change', ''].map((h, i) => (
                      <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((m) => {
                    const v = m.zoneMapLatest
                    return (
                      <TableRow key={m.id} hover onClick={() => open(m)} sx={{ cursor: 'pointer', '& td': { borderColor: C.lineSoft, py: 1.25 } }}>
                        <TableCell>
                          <Typography sx={TYPE.rowLabel}>{m.name}</Typography>
                          {m.companyName && m.companyName !== m.name && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{m.companyName}</Typography>}
                        </TableCell>
                        <TableCell>
                          {m.ownZoneMapPincodes
                            ? <ReadinessChip tone='pending' label={`${num(m.ownZoneMapPincodes)} pincode${m.ownZoneMapPincodes === 1 ? '' : 's'}`} />
                            : <ReadinessChip tone='none' label='Standard map' />}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: C.inkMuted }}>
                          {v ? `${actionLabel(v.action)} · ${fmtDate(v.createdAt)}${v.createdBy ? ` · ${v.createdBy}` : ''}` : '—'}
                        </TableCell>
                        <TableCell align='right'>
                          <Button size='small' variant='outlined' endIcon={<ChevronRightIcon />} onClick={(e) => { e.stopPropagation(); open(m) }} sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 }}>
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Card>
      )}

      <MerchantPickerDialog open={picking} onClose={() => setPicking(false)} merchants={all.filter((m) => !m.ownZoneMapPincodes)}
        title='Add a merchant override'
        helper='Pick the PTL merchant. It keeps the standard map until you upload its override file on the next screen.'
        onPick={(m) => { setPicking(false); open(m) }} />
    </PtlPage>
  )
}
